<template>
  <div class="ns-date-picker">
    <VMenu
      v-model="showMenu"
      :close-on-content-click="false"
      full-width
      lazy
      :min-width="width"
      content-class="ns-date-picker-wrapper"
    >
      <template v-slot:activator="{ on, attrs }">
        <VInput
          :value="pickerDate"
          :rules="rules"
          oncontextmenu="return false;"
          @paste="return false;"
        >
          <NsTextField
            ref="datePickerTextField"
            v-bind="attrs"
            :value="computedDateFormatted"
            :label="label"
            :clearable="clearable"
            :required="required"
            readonly
            @clear="handleClear"
            @blur="handleBlur"
            @keydown="handleKeyDown"
            @keyup="handleKeyUp"
            v-on="on"
          />
        </VInput>
      </template>
      <VDatePicker
        ref="picker"
        v-model="pickerDate"
        no-title
        :width="width"
        :first-day-of-week="firstDayOfWeek"
        :locale="locale"
        :color="color"
        :min="min"
        :max="maxDate"
        :type="type"
        next-icon="$vuetify.icons.date-next"
        prev-icon="$vuetify.icons.date-prev"
        @input="showMenu = false"
      ></VDatePicker>
    </VMenu>
  </div>
</template>

<script>
import moment from 'moment';
import { VMenu, VDatePicker, VInput } from 'vuetify/lib';
import NsTextField from './NsTextField.vue';
export default {
  components: {
    NsTextField,
    VMenu,
    VDatePicker,
    VInput,
  },
  props: {
    /**
     * Formats the date using momentJS. It will support all formats that momentJS supports.
     */
    dateFormat: {
      type: String,
      default: 'YYYY-MM-DD',
    },
    /**
     * Sets the max-width of the component
     */
    width: {
      type: [Number, String],
      default: 290,
    },
    /**
     * Sets the locale. It will suppor the Vuetify format of locales
     */
    locale: {
      type: String,
      default: 'en-US',
    },
    /**
     * Sets the first day of the week. 0 for Sunday and 1 for Monday.
     */
    firstDayOfWeek: {
      type: Number,
      default: 0,
    },
    /**
     * Sets the color. this accepts hex codes only.
     */
    color: {
      type: String,
      default: '#94D6E9',
    },
    /**
     * Sets the label for the text input
     */
    label: {
      type: String,
      default: null,
    },
    /**
     * Adds a 'x' icon to the text input.
     */
    clearable: Boolean,
    /**
     * The value  of the input
     */
    value: {
      type: String,
      default: null,
    },
    /**
     * Minimum date ie can not select dates before this date.
     */
    min: {
      type: String,
      default: '1900-01-01',
    },
    /**
     * Maximum date ie can not select dates after this date.
     */
    max: {
      type: String,
      default: undefined,
    },
    /**
     * Type is either 'date' or 'month'
     */
    type: {
      type: String,
      default: 'date',
    },
    /**  Birthdate option shows a picker designed for birthday dates and not future dates */
    birthdate: {
      type: Boolean,
      default: false,
    },
    /** Required field is optional */
    required: {
      type: Boolean,
      default: false,
    },
    /** When required is set to true this message will show underneath the date field */
    requiredErrorMessage: {
      type: String,
      default: 'This field is required',
    },
  },
  data() {
    return {
      /**
       * PickerDate is the value of the input
       */
      pickerDate: this.birthdate
        ? null
        : this.value != null
        ? this.value
        : new Date().toISOString().substr(0, 10),
      /**
       * If true shows the calendar
       */
      showMenu: false,
      rules: [(v) => Boolean(v) || !this.required || this.requiredErrorMessage],
    };
  },
  computed: {
    computedDateFormatted() {
      return this.pickerDate
        ? moment(this.pickerDate).locale(this.locale).format(this.dateFormat)
        : '';
    },
    date() {
      return this.value != null ? this.value : this.pickerDate;
    },
    maxDate() {
      return this.birthdate && !this.max
        ? new Date().toISOString().substr(0, 10)
        : this.max;
    },
  },
  watch: {
    /**
     * @event input
     * @type {string}
     */
    pickerDate(val) {
      this.$emit('input', val);
    },
    value(val) {
      this.pickerDate = val;
    },
    showMenu(val) {
      this.$emit('shown', val);
      if (this.birthdate && val) {
        setTimeout(() => (this.$refs.picker.activePicker = 'YEAR'));
      }
    },
  },
  methods: {
    /**
     * @event clear
     * @type {string}
     */
    handleClear() {
      this.pickerDate = '';
    },
    handleBlur(event) {
      const value = event.target.value;
      if (!value) {
        this.pickerDate = '';
      }
    },
    handleKeyDown(event) {
      if (event.key !== 'Tab') {
        event.preventDefault();
      }
    },
    handleKeyUp(event) {
      if (event.key !== 'Escape') {
        this.showMenu = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/vuetifyOverrides";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";
::v-deep {
  .v-date-picker-header,
  .v-btn__content,
  .v-date-picker-table table th {
    font-family: map.get(typographyVariables.$typography, font-family-primary);
  }
}
.ns-date-picker {
  font-family: map.get(typographyVariables.$typography, font-family-primary);
}
::v-deep {
  .v-btn--floating,
  .v-btn--icon::before {
    border-radius: 0;
  }
}
</style>
<style lang="scss">
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
.ns-date-picker-wrapper.v-menu__content.menuable__content__active {
  border-top: 1px solid !important;
  border-radius: 0px;
}
.v-date-picker-table .v-btn.v-btn--active {
  color: map.get(colors.$theme, 'primary');
}
</style>
